import {
  StepAction,
  WorkflowStepKind,
  WorkflowStepConfig,
  WorkflowStepTypeId,
  WORKFLOW_STEP_TYPE_IDS,
  WORKFLOW_ENGINE_STEP_TYPE_IDS,
} from 'src/@types/workflow/types';

export interface StepKindContract {
  stepTypeId: WorkflowStepTypeId;
  stepAction?: StepAction;
  hasMessage: boolean;
  hasAssignee: boolean;
  initialSettings?: () => WorkflowStepConfig;
}

export const DOCLOGIX_APPROVAL_CONFIRMATION_FIELD = 'FormApprovedInSigningPortal';

export const STEP_KIND_CONTRACT: Record<WorkflowStepKind, StepKindContract> = {
  signatureQualified: {
    stepTypeId: WORKFLOW_STEP_TYPE_IDS.signing,
    stepAction: 'qualified',
    hasMessage: false,
    hasAssignee: true,
  },
  signatureUnqualified: {
    stepTypeId: WORKFLOW_STEP_TYPE_IDS.signing,
    stepAction: 'unqualified',
    hasMessage: false,
    hasAssignee: true,
  },
  acknowledgement: {
    stepTypeId: WORKFLOW_STEP_TYPE_IDS.signing,
    stepAction: 'acknowledge',
    hasMessage: false,
    hasAssignee: true,
  },
  endorsement: {
    stepTypeId: WORKFLOW_STEP_TYPE_IDS.signing,
    stepAction: 'endorse',
    hasMessage: false,
    hasAssignee: true,
  },
  notification: {
    stepTypeId: WORKFLOW_STEP_TYPE_IDS.notify,
    hasMessage: true,
    hasAssignee: true,
  },
  doclogixApproval: {
    stepTypeId: WORKFLOW_STEP_TYPE_IDS.doclogixApproval,
    hasMessage: false,
    hasAssignee: false,
    initialSettings: () => ({ doclogixRecipients: 'none' }),
  },
  doclogixUpdate: {
    stepTypeId: WORKFLOW_STEP_TYPE_IDS.doclogixUpdate,
    hasMessage: false,
    hasAssignee: false,
    initialSettings: () => ({
      fields: [{ name: DOCLOGIX_APPROVAL_CONFIRMATION_FIELD, value: true }],
    }),
  },
};

export const ALL_STEP_KINDS = Object.keys(STEP_KIND_CONTRACT) as WorkflowStepKind[];

export const isEngineStepTypeId = (stepTypeId: string) =>
  Object.values<string>(WORKFLOW_ENGINE_STEP_TYPE_IDS).includes(stepTypeId);

export const STEP_TYPE_IDS_EVERY_TENANT_HAS: readonly string[] = [
  WORKFLOW_STEP_TYPE_IDS.signing,
  WORKFLOW_STEP_TYPE_IDS.notify,
];

const STEP_TYPE_IDS_THAT_CAN_BE_REJECTED: readonly string[] = [
  WORKFLOW_STEP_TYPE_IDS.signing,
  WORKFLOW_STEP_TYPE_IDS.doclogixApproval,
];

export function canStepKindBeRejected(kind: WorkflowStepKind): boolean {
  return STEP_TYPE_IDS_THAT_CAN_BE_REJECTED.includes(STEP_KIND_CONTRACT[kind].stepTypeId);
}

const STEP_TYPE_IDS_THAT_CANNOT_RUN_IN_PARALLEL: readonly string[] = [
  WORKFLOW_STEP_TYPE_IDS.doclogixApproval,
  WORKFLOW_STEP_TYPE_IDS.doclogixUpdate,
];

export function canStepKindRunInParallel(kind: WorkflowStepKind): boolean {
  return !STEP_TYPE_IDS_THAT_CANNOT_RUN_IN_PARALLEL.includes(STEP_KIND_CONTRACT[kind].stepTypeId);
}

const DEFAULT_SIGNING_ACTION: StepAction = 'qualified';

export function defaultConfigForKind(kind: WorkflowStepKind): WorkflowStepConfig {
  const { stepAction, hasMessage, initialSettings } = STEP_KIND_CONTRACT[kind];
  const config: WorkflowStepConfig = initialSettings?.() ?? {};

  if (stepAction) {
    config.action = stepAction;
  }
  if (hasMessage) {
    config.message = '';
  }

  return config;
}

export function stepKindsAvailableFor(stepTypeIds: readonly string[]): WorkflowStepKind[] {
  return ALL_STEP_KINDS.filter((kind) => stepTypeIds.includes(STEP_KIND_CONTRACT[kind].stepTypeId));
}

export function parseConfig(config: string | null): WorkflowStepConfig {
  if (!config) {
    return {};
  }

  try {
    return JSON.parse(config) as WorkflowStepConfig;
  } catch {
    return {};
  }
}

export function resolveStepKind(
  stepTypeId: string,
  config: WorkflowStepConfig
): WorkflowStepKind | null {
  if (stepTypeId === WORKFLOW_STEP_TYPE_IDS.signing) {
    const byAction = ALL_STEP_KINDS.find(
      (kind) => STEP_KIND_CONTRACT[kind].stepAction === (config.action ?? DEFAULT_SIGNING_ACTION)
    );

    return byAction ?? 'signatureQualified';
  }

  return ALL_STEP_KINDS.find((kind) => STEP_KIND_CONTRACT[kind].stepTypeId === stepTypeId) ?? null;
}

export function emailFromConfig(config: WorkflowStepConfig): string {
  return config.recipients?.[0] ?? '';
}
